import React, { useCallback, useRef } from 'react';
import { FieldArray } from 'react-final-form-arrays';

import FieldGroup from 'Shared/components/FormComponents/FieldGroup/FieldGroup';

import OnboardingWizardContentWrapper from '../OnboardingWizardWrappers/OnboardingWizardContentWrapper/OnboardingWizardContentWrapper';
import WorkspacesPreview from '../WorkspacesPreview/WorkspacesPreview';
import CheckboxItem from '../CheckboxItem/CheckboxItem';
import DiscussionTopicsListPreview from '../DiscussionTopics/DiscussionTopicsListPreview/DiscussionTopicsListPreview';
import { optionsDecorator, outputOptionsDecorator } from '../../utils';
import AddMoreTopicsButton from '../DiscussionTopics/AddMoreTopicsButton/AddMoreTopicsButton';
import { WorkspacesType, InvitationInfoType } from '../../types';
import { invitationTopicOptions } from '../../constants';

import '../DiscussionTopics/DiscussionTopics.scss';

type Props = {
  workspaces: WorkspacesType;
  discussionTopics: Array<string>;
  advanceOnboardingState: Function;
  invitationInfo: InvitationInfoType;
};

function topicsValidate(values) {
  if (values.some(item => item.checked)) return undefined;
  return 'Required field';
}

function DiscussionTopicsForInvited({
  workspaces: initialWorkspaces,
  discussionTopics: initialDiscussionTopics,
  advanceOnboardingState,
  invitationInfo,
}: Props) {
  const handleNextStep = useCallback(
    ({ discussionTopics, workspaces }) =>
      advanceOnboardingState({
        discussion_topics: outputOptionsDecorator(discussionTopics),
        workspaces: workspaces.filter(({ uuid, fullName }) => uuid && fullName),
      }),
    [advanceOnboardingState]
  );
  const ref = useRef(null);

  const handleAddMoreClick = useCallback(() => {
    const node = ref.current;
    if (node) node.focus();
  }, []);

  const inviterFullName = invitationInfo?.invited_by_full_name;
  const inviterAvatar = invitationInfo?.invited_by_avatar;
  const workspaceUuid = invitationInfo?.workspace_uuid;
  const workspaceName = invitationInfo?.workspace_name;
  const inviterDiscussionTopics = invitationInfo?.discussion_topics;
  const workspaceIsGroup = invitationInfo?.workspace_is_group;

  const inviterFirstName = inviterFullName?.split(' ')[0];
  return (
    <OnboardingWizardContentWrapper
      initialValues={{
        workspaces: [
          {
            uuid: workspaceUuid,
            isGroup: workspaceIsGroup,
            workspaceName,
            fullName: inviterFullName,
            avatar: inviterAvatar,
          },
          ...initialWorkspaces,
        ],
        discussionTopics: optionsDecorator({
          options: initialDiscussionTopics,
          presetOptions: invitationTopicOptions,
          freezeOptions: inviterDiscussionTopics,
        }),
      }}
      onNextStep={handleNextStep}
      previewSectionContent={<WorkspacesPreview workspaceContent={<DiscussionTopicsListPreview />} />}
    >
      <div className="onboarding-discussion-topics scrollbar pl-1">
        <h4 className="onboarding-form-title mb-3 padding-right-50">
          {inviterFirstName} invited you to a {workspaceIsGroup ? '' : '1:1'} workspace. Add discussion topics you need
          to discuss this week.
        </h4>
        <FieldGroup
          label="What topics should we add to this workspace?"
          labelClassName="onboarding-form-label pt-3 mb-3"
        >
          <div className="discussion-topics-list scrollbar padding-right-50">
            <FieldArray name="discussionTopics" validate={topicsValidate}>
              {({ fields }) =>
                fields.map((name, index) => (
                  <CheckboxItem
                    innerRef={index === fields.value.length - 1 ? ref : null}
                    index={index}
                    key={name}
                    name={name}
                    remove={fields.remove}
                    placeholder="Enter discussion topic"
                  />
                ))
              }
            </FieldArray>
          </div>
          <AddMoreTopicsButton onClick={handleAddMoreClick} />
        </FieldGroup>
      </div>
    </OnboardingWizardContentWrapper>
  );
}

export default DiscussionTopicsForInvited;
